import {
  CartDialog,
  ConditionsDialog,
  ShopifyIcon,
  type SelectionRuleCondition,
} from "@/components/products";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import type { MemberAddress } from "@/lib/queries/member-data";
import type { CartItem } from "@/lib/queries/products";
import { cn } from "@repo/ui/lib/utils";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { ContactInfo } from "./contact-info-section";

interface SelectionRule {
  minQuantity?: number | null;
  maxQuantity?: number | null;
  maxPrice?: number | null;
  label?: string | null;
  description?: string | null;
  conditions?: SelectionRuleCondition[] | null;
}

interface ProductSelectionHeaderProps {
  // Config
  title?: string;
  description?: ReactNode;

  // Slots
  backAction?: ReactNode;
  children?: ReactNode;

  // Cart & Logic
  effectiveSelectionRule?: SelectionRule | null;
  currencyCode: string; // Required for ConditionsDialog

  // Default Cart Props (Optional if cartAction is used)
  cart?: CartItem[];
  cartAction?: ReactNode;
  memberAddress?: MemberAddress | null;
  memberEmail?: string | null;
  isSubmitting?: boolean;
  onUpdateQuantity?: (id: string, quantity: number) => void;
  onRemoveItem?: (id: string) => void;
  onAddressChange?: (address: MemberAddress) => void;
  onSubmitOrder?: () => void;
  isCartOpen?: boolean;
  onOpenChange?: Dispatch<SetStateAction<boolean>>;
  profileAddresses?: Array<{
    id: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    isPrimary: boolean;
  }>;
  showPrice?: boolean;
  /** When false, hides Vendor badges throughout the selection flow. Driven by catalog `hideVendorsFromCreators`. */
  showVendor?: boolean;
  /** When false, hides Tag badges throughout the selection flow. Driven by catalog `hideTagsFromCreators`. */
  showTags?: boolean;
  contactInfoMode?: "same" | "different";
  onContactInfoModeChange?: (mode: "same" | "different") => void;
  contactInfo?: ContactInfo;
  onContactInfoChange?: (info: ContactInfo) => void;
  /** Whether the selected address has all required contact fields. */
  contactInfoComplete?: boolean;
  isShopifyType?: boolean;
  /** Inline validation errors from the last submit attempt. */
  submitErrors?: Partial<
    Record<"address" | "contact" | "cart" | "submit", string>
  >;
  /** Validation error from add-to-cart or update-quantity operations. */
  cartValidationError?: string | null;
  /** Brand-level minimum stock level to subtract from available inventory. */
  minimumStockLevel?: number;
}

export function ProductSelectionHeader({
  title = "Product Selection",
  description,
  backAction,
  children,
  effectiveSelectionRule,
  currencyCode,
  cart,
  cartAction,
  memberAddress = null,
  memberEmail = null,
  isSubmitting = false,
  onUpdateQuantity = () => {},
  onRemoveItem = () => {},
  onAddressChange = () => {},
  onSubmitOrder = () => {},
  isCartOpen = false,
  onOpenChange = () => {},
  profileAddresses,
  showPrice = true,
  showVendor = true,
  showTags = true,
  contactInfoMode,
  onContactInfoModeChange,
  contactInfo,
  onContactInfoChange,
  contactInfoComplete,
  isShopifyType,
  submitErrors,
  cartValidationError,
  minimumStockLevel = 0,
}: ProductSelectionHeaderProps) {
  return (
    <div className="sticky top-[var(--header-height)] z-30 bg-background transition-[top] duration-200 ease-linear">
      <div className="px-4 sm:px-6 py-3">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {backAction && <div className="mt-1">{backAction}</div>}

            <ProjectPageHeader
              title={
                isShopifyType ? (
                  <span className="inline-flex items-center gap-2">
                    {title}
                    <ShopifyIcon className="h-6 w-6 shrink-0" />
                  </span>
                ) : (
                  title
                )
              }
              subtitle={description}
              className="flex-col items-start mb-0 flex-1 min-w-0"
              actionsClassName="w-full md:w-auto"
            >
              <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
                {/* Extra Actions (e.g. Menu) */}
                {children}

                {/* Conditions Dialog */}
                {effectiveSelectionRule && (
                  <div className="flex-1 md:flex-none">
                    <ConditionsDialog
                      selectionRule={effectiveSelectionRule}
                      currencyCode={currencyCode}
                      showPrice={showPrice}
                      cart={cart ?? []}
                      triggerClassName="w-full"
                      isShopifyType={isShopifyType}
                    />
                  </div>
                )}

                {/* Cart Action (Custom or Default) */}
                <div
                  className={cn(
                    "md:flex-none",
                    effectiveSelectionRule ? "flex-1" : "w-full",
                  )}
                >
                  {cartAction
                    ? cartAction
                    : cart && (
                        <CartDialog
                          cart={cart}
                          currencyCode={currencyCode}
                          showPrice={showPrice}
                          showVendor={showVendor}
                          showTags={showTags}
                          selectionRule={effectiveSelectionRule ?? undefined}
                          memberAddress={memberAddress}
                          memberEmail={memberEmail}
                          isSubmitting={isSubmitting}
                          onUpdateQuantity={onUpdateQuantity}
                          onRemoveItem={onRemoveItem}
                          onAddressChange={onAddressChange}
                          onSubmitOrder={onSubmitOrder}
                          open={isCartOpen}
                          onOpenChange={onOpenChange}
                          triggerClassName="w-full"
                          profileAddresses={profileAddresses}
                          contactInfoMode={contactInfoMode}
                          onContactInfoModeChange={onContactInfoModeChange}
                          contactInfo={contactInfo}
                          onContactInfoChange={onContactInfoChange}
                          contactInfoComplete={contactInfoComplete}
                          submitErrors={submitErrors}
                          cartValidationError={cartValidationError}
                          minimumStockLevel={minimumStockLevel}
                        />
                      )}
                </div>
              </div>
            </ProjectPageHeader>
          </div>
        </div>
      </div>
    </div>
  );
}
